import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Play, ChevronsDown } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { mediaUrl, heroVideoFor } from './mainContent';

// 미디어 박스 안을 채움 (object-cover · position 으로 인물·플랫폼 표시 유지)
const HeroMedia = ({ image, video, position, onFail }) => {
  const style = { objectPosition: position };
  if (video) {
    return (
      <video key={video} src={video} poster={image || undefined} autoPlay muted loop playsInline preload="auto"
        onError={() => onFail(video)} className="absolute inset-0 w-full h-full object-cover" style={style}
        data-testid="main-hero-video" />
    );
  }
  return image ? (
    <img src={image} alt="LIVEMOA 홍보 이미지" className="absolute inset-0 w-full h-full object-cover" style={style}
      data-testid="main-hero-image" />
  ) : null;
};

const useHeroVideo = (content, isPc) => {
  const [failed, setFailed] = useState(null);
  const raw = heroVideoFor(content, isPc);
  return [raw && raw !== failed ? raw : null, setFailed];
};

// 소개 영역 · 문구형(기본) 또는 관리자 이미지형 (이미지 없거나 로드 실패 시 문구형)
const Intro = ({ content, mobile }) => {
  const [broken, setBroken] = useState(null);
  const src = content?.intro_mode === 'image' ? mediaUrl(content?.intro_image) : null;
  if (!src || broken === src) return <Headline mobile={mobile} />;
  return (
    <img src={src} alt="LIVEMOA 소개" onError={() => setBroken(src)} data-testid={mobile ? 'main-intro-image-mobile' : 'main-intro-image'}
      className={`block w-full h-auto object-contain ${mobile ? 'max-h-[46svh]' : 'max-h-[calc((100svh-80px)*0.8-170px)]'}`} />
  );
};

const Headline = ({ mobile }) => (
  <>
    <p className={`text-[#4B5280] font-semibold ${mobile ? 'text-[14px]' : 'text-[18px] xl:text-[20px]'}`}>라이브 커머스의 모든 과정을 하나로</p>
    <h1 className={`mt-3 text-[#1B2140] font-black leading-[1.22] tracking-tight break-keep ${mobile ? 'text-[28px] min-[400px]:text-[31px]' : 'text-[clamp(22px,1.9vw,36px)]'}`}>
      방송부터 상품, 주문, 결제,<br />배송, 고객관리까지<br />
      <span className="bg-gradient-to-r from-[#1FB6FF] via-[#3D6BFF] to-[#8B3DFF] bg-clip-text text-transparent">LIVEMOA</span>와 함께!
    </h1>
    <p className={`mt-4 text-[#5B6185] leading-relaxed ${mobile ? 'text-[14px]' : 'text-[16px] xl:text-[18px]'}`}>
      더 쉽고, 더 강력한 올인원 라이브 커머스 플랫폼으로<br />지금, 새로운 매출의 기회를 만들어보세요.
    </p>
  </>
);

const StartBtn = ({ mobile }) => (
  <Link to="/seller/apply" data-testid={mobile ? 'main-hero-start-btn-mobile' : 'main-hero-start-btn'}
    className={`inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-[#19B6FF] via-[#3D6BFF] to-[#8B3DFF] text-white font-bold shadow-[0_8px_24px_rgba(61,107,255,0.35)] hover:brightness-110 transition-[filter] ${mobile ? 'w-full h-[52px] text-[17px]' : 'h-14 px-7 text-[17px] xl:text-[18px]'}`}>
    지금 무료로 시작하기 <ArrowRight className="w-5 h-5" />
  </Link>
);

const VideoBtn = ({ mobile, onClick }) => (
  <button type="button" onClick={onClick} data-testid={mobile ? 'main-hero-video-btn-mobile' : 'main-hero-video-btn'}
    className={`inline-flex items-center justify-center gap-2 rounded-2xl border-2 border-[#C9CFEA] bg-white text-[#1B2140] font-bold hover:border-[#3D6BFF] transition-colors ${mobile ? 'w-full h-[52px] text-[16px]' : 'h-14 px-6 text-[17px]'}`}>
    <Play className="w-5 h-5 fill-[#1B2140]" /> 서비스 소개 영상
  </button>
);

// PC · 왼쪽 1/3 소개 + 오른쪽 2/3 둥근 미디어
export const HeroPC = ({ content, height = 'calc((100svh - 80px) * 0.8)', onPlay }) => {
  const [video, setFailed] = useHeroVideo(content, true);
  return (
    <section className="w-full bg-gradient-to-b from-[#F4F6FF] to-white" style={{ height, minHeight: 520 }} data-testid="main-hero-pc">
      <div className="h-full max-w-[1600px] mx-auto px-10 xl:px-14 py-6 grid grid-cols-[1fr_2fr] gap-8 xl:gap-12 items-center">
        <div className="main-fade-up min-w-0" data-testid="main-hero-pc-intro">
          <Intro content={content} />
          <div className="mt-8 flex flex-wrap gap-3" style={{ animationDelay: '120ms' }}>
            <StartBtn />{video && <VideoBtn onClick={() => onPlay(video)} />}
          </div>
        </div>
        <div className="relative h-full rounded-[28px] overflow-hidden bg-[#7E8EDA] shadow-[0_20px_50px_rgba(40,50,140,0.18)] main-fade-up"
          style={{ animationDelay: '80ms' }} data-testid="main-hero-pc-media">
          <HeroMedia image={mediaUrl(content?.hero_image_pc)} video={video} position="62% 30%" onFail={setFailed} />
        </div>
      </div>
    </section>
  );
};

// 모바일 · 대형 미디어 → 소개 → 버튼 (세로 배치)
export const HeroMobile = ({ content, onPlay }) => {
  const [video, setFailed] = useHeroVideo(content, false);
  return (
    <section className="w-full bg-gradient-to-b from-[#F4F6FF] to-white pt-2 pb-8" data-testid="main-hero-mobile">
      <div className="mx-2 relative aspect-[4/5] rounded-[22px] overflow-hidden bg-[#7E8EDA] shadow-[0_14px_36px_rgba(40,50,140,0.18)] main-fade-up"
        data-testid="main-hero-mobile-media">
        <HeroMedia image={mediaUrl(content?.hero_image_mobile)} video={video} position="60% 62%" onFail={setFailed} />
      </div>
      <div className="mt-6 px-5 main-fade-up" style={{ animationDelay: '100ms' }}><Intro content={content} mobile /></div>
      <div className="mt-6 px-5 space-y-2.5 main-fade-up" style={{ animationDelay: '160ms' }}>
        <StartBtn mobile />{video && <VideoBtn mobile onClick={() => onPlay(video)} />}
      </div>
      <ChevronsDown className="mx-auto mt-4 w-6 h-6 text-[#8A90B8] animate-bounce" />
    </section>
  );
};

export const MainHero = ({ content }) => {
  const [video, setVideo] = useState(null);
  return (
    <>
      <div className="hidden md:block"><HeroPC content={content} onPlay={setVideo} /></div>
      <div className="md:hidden"><HeroMobile content={content} onPlay={setVideo} /></div>
      <Dialog open={!!video} onOpenChange={(o) => { if (!o) setVideo(null); }}>
        <DialogContent className="max-w-4xl p-0 bg-black border-0 overflow-hidden" data-testid="main-video-dialog">
          <DialogTitle className="sr-only">서비스 소개 영상</DialogTitle>
          {video && <video src={video} controls autoPlay playsInline className="w-full max-h-[80vh]" />}
        </DialogContent>
      </Dialog>
    </>
  );
};
